// Modules
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _findIndex from 'lodash/findIndex';

import { LeftCaretIcon } from '../SvgIcons/LeftCaret';
import { RightCaretIcon } from '../SvgIcons/RightCaret';

import './tabs.css';

// keyboard key codes
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const TAB = 9;

/** Same responsive helper function as in zola-helpers
 */
const isMobile = () => {
  return window.matchMedia(`(max-width: 767px)`).matches;
};

/** Keyboard-accessible UI component for tab menu and panels
 */
const Tabs = ({
  arrowVariant,
  smallVariant,
  tabItems,
  initialTabId,
  noVerticalScroll,
  classNames,
}) => {
  const tabList = useRef(null);
  const [currentTabId, setCurrentTabId] = useState(initialTabId || tabItems[0].id);
  const [showFocusOutline, setShowFocusOutline] = useState(false);
  const [showArrows, setShowArrows] = useState(false);
  const currentTabIndex = _findIndex(tabItems, (i) => i.id === currentTabId);
  const [disabledArrows, setDisabledArrows] = useState({
    left: false,
    right: false,
  });

  /**
   * Hook to run on initial load, which determines whether to display, enable,
   * or disable arrows for manual jump scrolling
   */
  useEffect(() => {
    if (arrowVariant) {
      const el = tabList.current;
      if (el) {
        if (el.scrollWidth > el.offsetWidth) {
          setShowArrows(true);
        }
        if (currentTabIndex === 0) {
          // if it is the first item disable previous arrow
          setDisabledArrows({ left: true, right: false });
        } else if (currentTabIndex === tabItems.length - 1) {
          // if it is the last item disable next arrow
          setDisabledArrows({ left: false, right: true });
        } else {
          setDisabledArrows({ left: false, right: false });
        }
      }
    }
  }, [tabList, arrowVariant, currentTabIndex, tabItems.length]);

  /**
   * Hook to update currentTabId if the initialTabId changes during this
   * component's lifecycle. This could happen if the parent needs to control which
   * tab is active, in addition to this component controlling it. A practical example
   * of this is if the parent has an onClick property for each tab, which changes
   * the url with history.push. When a user clicks back and forward to navigate
   * between tabs in the browser, this Tabs component has no way of knowing that
   * the current tab should be changing. The parent needs to pass down a new
   * current tab (via initialTabId) so that this component can set it.
   */
  useEffect(() => {
    if (initialTabId) setCurrentTabId(initialTabId);
  }, [initialTabId]);

  /**
   * Animate clicked tab into full view if mobile, call the tab's
   * custom click handler if it was passed down from props, and set the
   * clicked tab's id in state
   * @param {Object} e - event
   * @param {String} id - id of the element clicked
   */
  const handleTabClick = (e, id) => {
    const currentTab = tabItems.find((i) => i.id === id);

    if (typeof window !== 'undefined' && isMobile()) {
      e.target.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }

    setCurrentTabId(id);

    if (currentTab.onClick) {
      currentTab.onClick();
    }
  };

  /**
   * Show tab nav focus ring for keyboard users, enable using left and right
   * keyboard arrows to change tab panel view if parent element is in focus
   * @param {Object} e - event
   */
  const onKeyUp = (e) => {
    // show focus ring if tabbed over
    if (e.keyCode === TAB) {
      setShowFocusOutline(true);
    }

    if (e.keyCode === LEFT_ARROW || e.keyCode === RIGHT_ARROW) {
      let targetTabIndex = 0;

      // select next tab if keyboard right arrow is pressed &
      // cycle back to the beginning if at the end
      if (e.keyCode === RIGHT_ARROW) {
        if (currentTabIndex + 1 < tabItems.length) {
          targetTabIndex = currentTabIndex + 1;
        }
      }

      // select prev tab if keyboard left arrow is pressed &
      // cycle back to the end if at the beginning
      if (e.keyCode === LEFT_ARROW) {
        if (currentTabIndex - 1 >= 0) {
          targetTabIndex = currentTabIndex - 1;
        } else {
          targetTabIndex = tabItems.length - 1;
        }
      }

      handleTabClick(e, tabItems[targetTabIndex].id);
    }
  };

  /**
   * When desktop pagination arrows are displayed, move through selected tabs
   * and keep them in view
   * @param {Number} positionIndex - new index to move to
   */
  const setNewPositionAndScroll = (positionIndex) => {
    const el = tabList.current;
    const scrollProperties = { behavior: 'smooth', block: noVerticalScroll ? 'nearest' : 'start' };

    if (positionIndex === tabItems.length - 2) {
      // make sure the last item scrolls into view
      scrollProperties.inline = 'start';
    } else if (positionIndex === 1) {
      // make sure the first item scrolls into view
      scrollProperties.inline = 'end';
    } else {
      scrollProperties.inline = 'nearest';
    }

    setCurrentTabId(tabItems[positionIndex].id);
    el.children[positionIndex].scrollIntoView(scrollProperties);
  };

  const goToPreviousTab = () => {
    const newTabIndex = currentTabIndex - 1;
    if (newTabIndex >= 0) setNewPositionAndScroll(newTabIndex);
  };

  const goToNextTab = () => {
    const newTabIndex = currentTabIndex + 1;
    if (currentTabIndex <= tabItems.length) setNewPositionAndScroll(newTabIndex);
  };

  return (
    <div
      className={cx('zola-ui tab-component', classNames, smallVariant ? 'small-tab-component' : '')}
    >
      <div className="tabs-list-container">
        <div className="tab-overlay">
          <div className={cx('fade-edges', showArrows ? 'narrow' : '')} />
          {showArrows && (
            <div className="tab-arrows">
              <button
                type="button"
                disabled={disabledArrows.left}
                className="tab-arrow"
                onClick={goToPreviousTab}
              >
                <LeftCaretIcon className="arrow-icon" />
              </button>
              <button
                type="button"
                disabled={disabledArrows.right}
                className="tab-arrow"
                onClick={goToNextTab}
              >
                <RightCaretIcon className="arrow-icon" />
              </button>
            </div>
          )}
        </div>
        <ul
          role="tablist"
          className={cx(
            'tabs-list',
            showFocusOutline ? 'show-focus-outline' : '',
            showArrows ? 'with-arrows' : ''
          )}
          onKeyUp={onKeyUp}
          tabIndex={0}
          ref={tabList}
        >
          {tabItems.map((tab, index) => {
            const isCurrentTab = currentTabId === tab.id;
            return (
              <li
                role="tab"
                className={cx('tab', isCurrentTab ? 'current-tab' : '')}
                id={`tab-${index}`}
                aria-controls={`panel-${index}`}
                aria-selected={isCurrentTab}
                onClick={(e) => handleTabClick(e, tab.id)}
                key={tab.id}
              >
                {tab.text}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="tab-panels">
        {tabItems.map((tab, index) => {
          return (
            <div
              id={`panel-${index}`}
              role="tabpanel"
              aria-labelledby={`tab-${index}`}
              hidden={currentTabId !== tab.id}
              key={tab.id}
            >
              <div className="panel-content">{tab.panelContent}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

Tabs.propTypes = {
  /** array of tab data */
  tabItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      onClick: PropTypes.func, // optional function to be called when a tab is selected. see TABS_WITH_COMPONENTS_IN_PANEL in ./mockItems.jsx for an example usage
      panelContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    })
  ).isRequired,
  /** id for tab that will be initially selected */
  initialTabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** flag for showing pagination arrows on desktop. this particular prop can't be used on server-rendered pages because it relies on browser web api values. see more info in example #5 on the storybook overview page. */
  arrowVariant: PropTypes.bool,
  /** flag for using small typeface */
  smallVariant: PropTypes.bool,
  /** remove vertical scroll on left/right caret button clicks */
  noVerticalScroll: PropTypes.bool,
  /** any additional classes */
  classNames: PropTypes.string,
};

export default Tabs;
